import React, { forwardRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { fontFamily, color, radius, transition } from "./tokens";

const SIZE_MAP = {
  extraSmall: { height: 24, paddingX: 8,  fontSize: 13 },
  small:      { height: 32, paddingX: 10, fontSize: 14 },
  medium:     { height: 40, paddingX: 12, fontSize: 14 },
  large:      { height: 48, paddingX: 16, fontSize: 16 },
};

const useStyles = makeStyles(() => ({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    width: ({ fullWidth }) => (fullWidth ? "100%" : "fit-content"),
    boxSizing: "border-box",
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: color.text.primary,
    fontFamily: fontFamily.base,
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    height: ({ size }) => SIZE_MAP[size]?.height ?? 40,
    padding: ({ size }) => `0 ${SIZE_MAP[size]?.paddingX ?? 12}px`,
    fontSize: ({ size }) => SIZE_MAP[size]?.fontSize ?? 14,
    fontFamily: fontFamily.base,
    color: color.text.primary,
    fontWeight: "normal",
    backgroundColor: color.surface.card,
    border: `1px solid ${color.border.strong}`,
    borderRadius: radius.sm,
    outline: "none",
    transition: `border-color ${transition.base}`,
    "&:hover": {
      borderColor: "#BDBDBD",
    },
    "&:focus": {
      borderColor: color.border.interactive,
    },
    "&:disabled": {
      backgroundColor: color.surface.disabled,
      cursor: "not-allowed",
    },
    "&[type='number']::-webkit-inner-spin-button, &[type='number']::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
    },
    "&[type='number']": {
      MozAppearance: "textfield",
    },
  },
  inputError: {
    borderColor: `${color.status.error} !important`,
    "&:hover": {
      borderColor: `${color.status.error} !important`,
    },
  },
  helperText: {
    fontSize: 12,
    fontFamily: fontFamily.base,
    color: color.text.secondary,
  },
  helperTextError: {
    color: color.status.error,
  },
}));

const TextInput = forwardRef(({
  label,
  helperText,
  error = false,
  size = "medium",
  fullWidth = false,
  type = "text",
  value,
  onChange,
  placeholder,
  disabled = false,
  autoFocus = false,
  onKeyDown,
  inputProps = {},
  style,
}, ref) => {
  const classes = useStyles({ size, fullWidth });

  return (
    <div className={classes.root} style={style}>
      {label && <span className={classes.label}>{label}</span>}
      <input
        ref={ref}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        className={`${classes.input}${error ? ` ${classes.inputError}` : ""}`}
        {...inputProps}
      />
      {helperText && (
        <span className={`${classes.helperText}${error ? ` ${classes.helperTextError}` : ""}`}>
          {helperText}
        </span>
      )}
    </div>
  );
});

TextInput.displayName = "TextInput";

export default TextInput;
